import type { SessionMemberProfile } from "./sessionTypes";

/** Journal editorial staff roles (Phase 1). */
export const EDITORIAL_JOURNAL_ROLES = ["editor_in_chief", "editor"] as const;

export function hasGlobalRole(globalRoles: string[], role: string): boolean {
  return globalRoles.includes(role);
}

export function hasAnyGlobalRole(globalRoles: string[], roles: string[]): boolean {
  return roles.some((r) => globalRoles.includes(r));
}

export function hasJournalRole(
  journalMemberships: Array<{ journal_role: string }>,
  roles: string[],
): boolean {
  return journalMemberships.some((m) => roles.includes(m.journal_role));
}

export function hasJournalRoleForJournal(
  journalMemberships: Array<{ journal_id: string; journal_role: string }>,
  journalId: string,
  roles: string[],
): boolean {
  return journalMemberships.some(
    (m) => m.journal_id === journalId && roles.includes(m.journal_role),
  );
}

export function canManageEditorialBoard(input: {
  globalRoles: string[];
  journalMemberships: Array<{ journal_id: string; journal_role: string }>;
  journalId: string;
}): boolean {
  if (hasGlobalRole(input.globalRoles, "admin")) return true;
  return hasJournalRoleForJournal(input.journalMemberships, input.journalId, ["editor_in_chief"]);
}

export function hasEditorialJournalRole(
  journalMemberships: Array<{ journal_role: string }>,
): boolean {
  return hasJournalRole(journalMemberships, [...EDITORIAL_JOURNAL_ROLES]);
}

export function isActiveMember(memberProfile: SessionMemberProfile | null): boolean {
  return memberProfile?.membership_status === "active";
}

/**
 * Eligible to open the normal Propose Journal page (/journals/propose).
 * Does NOT include generic pending Author/Reviewer applicants.
 * First-time Journal Editor proposals are created during /register onboarding,
 * not via this gate.
 */
export function canProposeJournal(input: {
  globalRoles: string[];
  journalMemberships: Array<{ journal_role: string }>;
  memberProfile: SessionMemberProfile | null;
}): boolean {
  if (hasGlobalRole(input.globalRoles, "admin")) return true;
  if (hasEditorialJournalRole(input.journalMemberships)) return true;
  if (isActiveMember(input.memberProfile)) return true;
  return false;
}

export function deriveDefaultLanding(input: {
  globalRoles: string[];
  journalMemberships?: Array<{ journal_role: string }>;
  memberProfile?: SessionMemberProfile | null;
}): string {
  // Journals Director (global admin) keeps the admin landing.
  if (hasGlobalRole(input.globalRoles, "admin")) return "/admin";
  return "/dashboard";
}
